import {useEffect,useRef,useState} from 'react';
import {courseApi} from '../course-api';
import {startPlaybackRenewal} from '../course-playback-renewal';
import {coursePlaybackQueue} from '../reader-media';

/** The course-page player owns one server lease. A failed heartbeat never
 * reacquires it: takeover requires the student's explicit action. */
export function useCoursePlayback(courseId:string,lessonId:string|undefined,onLost:()=>void){
  const [state,setState]=useState<{lessonId?:string;url?:string;busy:boolean;error?:string}>({busy:false});
  const lost=useRef(onLost);lost.current=onLost;
  const pagePlayerId=useRef(crypto.randomUUID());
  const current=useRef<{lessonId:string;playerId:string;leaseId?:string;active:boolean;attempt:number;stopRenewal?:()=>void}|null>(null);
  const release=(value:{lessonId:string;playerId:string;leaseId?:string})=>{if(value.leaseId){const leaseId=value.leaseId;void coursePlaybackQueue.run(signal=>courseApi.releasePlayback(courseId,value.lessonId,value.playerId,leaseId,signal)).catch(()=>{});}};
  const acquire=useRef<(takeover:boolean)=>Promise<void>>(async()=>{});
  acquire.current=async(takeover:boolean)=>{
    const lease=current.current;if(!lease?.active)return;
    const attempt=++lease.attempt;lease.stopRenewal?.();setState({lessonId:lease.lessonId,busy:true});
    const operation=async(signal:AbortSignal)=>{if(current.current!==lease||!lease.active||lease.attempt!==attempt)return;try{
      const requestedAt=performance.now(),result=await courseApi.playback(courseId,lease.lessonId,lease.playerId,takeover,signal);
      if(current.current!==lease||!lease.active||lease.attempt!==attempt){await courseApi.releasePlayback(courseId,lease.lessonId,lease.playerId,result.leaseId,signal);return;}
      lease.leaseId=result.leaseId;
      setState({lessonId:lease.lessonId,url:result.url,busy:false});
      lease.stopRenewal=startPlaybackRenewal({initial:result,requestedAt,
        renew:signal=>courseApi.renewPlayback(courseId,lease.lessonId,lease.playerId,result.leaseId,signal),
        onLost:error=>{if(!lease.active||current.current!==lease||lease.attempt!==attempt)return;lost.current();setState({lessonId:lease.lessonId,busy:false,error});},
      });
    }catch(error){
      if(current.current===lease&&lease.active&&lease.attempt===attempt){lost.current();setState({lessonId:lease.lessonId,busy:false,error:error instanceof Error?error.message:'暂时无法播放，请重试。'});}
    }};
    await coursePlaybackQueue.run(operation);
  };
  useEffect(()=>{
    if(!lessonId){setState({busy:false});return;}
    const lease={lessonId,playerId:pagePlayerId.current,leaseId:undefined as string|undefined,active:true,attempt:0,stopRenewal:undefined as (()=>void)|undefined};current.current=lease;
    void acquire.current(false);
    const pagehide=()=>{++lease.attempt;lease.stopRenewal?.();lost.current();setState({lessonId:lease.lessonId,busy:false});release(lease);};
    const pageshow=(event:PageTransitionEvent)=>{if(event.persisted&&lease.active)void acquire.current(false);};
    window.addEventListener('pagehide',pagehide);
    window.addEventListener('pageshow',pageshow);
    return()=>{lease.active=false;lease.stopRenewal?.();window.removeEventListener('pagehide',pagehide);window.removeEventListener('pageshow',pageshow);release(lease);if(current.current===lease)current.current=null;};
  },[courseId,lessonId]);
  return {url:state.lessonId===lessonId?state.url:undefined,busy:!!lessonId&&(state.lessonId!==lessonId||state.busy),error:state.lessonId===lessonId?state.error:undefined,retry:()=>acquire.current(true)};
}
